import { supabase } from '../lib/supabase';

/**
 * Discount Code Service
 * All validation/redemption logic runs server-side via SECURITY DEFINER
 * Postgres functions (validate_discount_code / redeem_discount_code), so no
 * discount code data or business rules are exposed directly to the client.
 */
export const discountCodeService = {
  /**
   * Validates a discount code against a given (pre-VAT) amount.
   * Throws an Error with a user-facing Arabic message on failure.
   * @param {string} code
   * @param {number} amount - subtotal before VAT, used to compute the discount amount
   * @returns {Promise<{code: string, discountPercent: number, discountAmount: number}>}
   */
  async validateCode(code, amount) {
    const normalized = String(code || '').trim();
    if (!normalized) {
      throw new Error('يرجى إدخال كود الخصم');
    }

    const { data, error } = await supabase.rpc('validate_discount_code', {
      p_code: normalized,
      p_amount: amount,
    });

    if (error) {
      console.error('validateCode error:', error);
      throw new Error('حدث خطأ أثناء التحقق من كود الخصم. حاول مرة أخرى.');
    }

    if (!data?.ok) {
      throw new Error(data?.message || 'كود الخصم غير صحيح');
    }

    return {
      code: data.code,
      discountPercent: Number(data.discount_percent),
      discountAmount: Number(data.discount_amount),
    };
  },

  /**
   * Marks a discount code as used for a specific booking. Called only after
   * the booking has been created successfully. Never throws — a failure here
   * must not block or roll back an already-created booking.
   * @param {string} code
   * @param {string} bookingId
   * @param {number} discountAmount
   */
  async redeemCode(code, bookingId, discountAmount) {
    try {
      const { data, error } = await supabase.rpc('redeem_discount_code', {
        p_code: code,
        p_booking_id: bookingId,
        p_discount_amount: discountAmount,
      });
      if (error) {
        console.warn('redeemCode error (non-blocking):', error);
        return { ok: false };
      }
      return data;
    } catch (err) {
      console.warn('redeemCode unexpected error (non-blocking):', err);
      return { ok: false };
    }
  },
};

export default discountCodeService;
